import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import { FiCheck, FiKey } from "react-icons/fi";
import { TbUsb } from "react-icons/tb";
import {
  classifyWebAuthnError,
  isWebAuthnRetryableError,
  WEBAUTHN_TIMEOUT_MINUTES,
  WEBAUTHN_TIMEOUT_MS,
} from "../../../Services/webauthnClient";

type FidoTouchPhase = "idle" | "waiting" | "success" | "error";

const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export interface FidoTouchPanelProps {
  onWebAuthn: () => Promise<void>;
  onSuccess?: () => void | Promise<void>;
  onError?: (message: string) => void;
  onWebAuthnStateChange?: (inProgress: boolean) => void;
  disabled?: boolean;
  successMessage?: string;
  idleMessage?: string;
  buttonLabel?: string;
  waitingHint?: string;
  className?: string;
}

export const FidoTouchPanel = ({
  onWebAuthn,
  onSuccess,
  onError,
  onWebAuthnStateChange,
  disabled = false,
  successMessage = "Chave verificada com sucesso!",
  idleMessage = "Chave USB deve estar conectada. Toque no botão quando o navegador solicitar.",
  buttonLabel = "Usar chave USB",
  waitingHint = `Digite o PIN na chave e toque no botão — você tem até ${WEBAUTHN_TIMEOUT_MINUTES} minutos`,
  className = "",
}: FidoTouchPanelProps) => {
  const [phase, setPhase] = useState<FidoTouchPhase>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [troubleshooting, setTroubleshooting] = useState("");
  const [canRetry, setCanRetry] = useState(false);
  const [remainingMs, setRemainingMs] = useState(WEBAUTHN_TIMEOUT_MS);
  const runningRef = useRef(false);

  /**
   * WebAuthn must start in the same user-activation turn as the click.
   * Kick off onWebAuthn() synchronously, then await the returned promise.
   */
  const runWebAuthn = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      if (runningRef.current || disabled) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[FidoTouchPanel] ceremony blocked — already running or disabled");
        }
        return;
      }
      runningRef.current = true;
      setPhase("waiting");
      setErrorMessage("");
      setTroubleshooting("");
      setCanRetry(false);
      setRemainingMs(WEBAUTHN_TIMEOUT_MS);
      onWebAuthnStateChange?.(true);

      const ceremony = onWebAuthn();

      void ceremony
        .then(async () => {
          setPhase("success");
          await onSuccess?.();
        })
        .catch((error) => {
          const details = classifyWebAuthnError(error);
          setErrorMessage(details.message);
          setTroubleshooting(details.troubleshooting ?? "");
          setCanRetry(isWebAuthnRetryableError(error));
          setPhase("error");
          onError?.(details.message);
        })
        .finally(() => {
          runningRef.current = false;
          onWebAuthnStateChange?.(false);
        });

      event?.currentTarget?.blur();
    },
    [disabled, onError, onSuccess, onWebAuthn, onWebAuthnStateChange]
  );

  useEffect(() => {
    if (phase !== "waiting") return undefined;

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setRemainingMs(Math.max(0, WEBAUTHN_TIMEOUT_MS - elapsed));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [phase]);

  const isWaiting = phase === "waiting";
  const isSuccess = phase === "success";
  const isError = phase === "error";

  return (
    <div className={`fido-touch-panel ${className}`.trim()}>
      <div
        className={`fido-touch-panel__icon-wrap ${
          isWaiting ? "fido-touch-panel__icon-wrap--pulse" : ""
        } ${isSuccess ? "fido-touch-panel__icon-wrap--success" : ""} ${
          isError ? "fido-touch-panel__icon-wrap--error" : ""
        }`}
      >
        {isSuccess ? (
          <FiCheck className="h-8 w-8" aria-hidden />
        ) : (
          <TbUsb className="h-8 w-8" aria-hidden />
        )}
      </div>

      <p className="fido-touch-panel__title">
        {isSuccess
          ? successMessage
          : isError
            ? errorMessage || "Não foi possível verificar a chave. Tente novamente."
            : isWaiting
              ? "Aguardando toque na chave USB…"
              : idleMessage}
      </p>

      {isWaiting ? (
        <>
          <p className="fido-touch-panel__hint">{waitingHint}</p>
          <p className="fido-touch-panel__windows-hint">
            No Windows, confira se o diálogo de segurança do sistema não abriu atrás
            do navegador — use Alt+Tab para encontrá-lo e conclua o PIN e o toque na
            chave.
          </p>
          <p
            className="fido-touch-panel__countdown"
            role="timer"
            aria-live="polite"
            aria-atomic="true"
          >
            Tempo restante: {formatCountdown(remainingMs)}
          </p>
        </>
      ) : null}

      {isWaiting ? (
        <div className="fido-touch-panel__spinner" role="status" aria-label="Aguardando">
          <span className="login-submit-btn__spinner" />
        </div>
      ) : null}

      {isError && troubleshooting ? (
        <p className="fido-touch-panel__troubleshooting">{troubleshooting}</p>
      ) : null}

      {phase === "idle" ? (
        <button
          type="button"
          className="login-verification-btn login-submit-btn fido-touch-panel__action"
          onClick={runWebAuthn}
          disabled={disabled}
        >
          <FiKey className="h-4 w-4" aria-hidden />
          {buttonLabel}
        </button>
      ) : null}

      {isError && canRetry ? (
        <button
          type="button"
          className="login-verification-btn login-submit-btn fido-touch-panel__action"
          onClick={(event) => {
            setErrorMessage("");
            setTroubleshooting("");
            runWebAuthn(event);
          }}
          disabled={disabled}
        >
          Tentar novamente
        </button>
      ) : isError ? (
        <button
          type="button"
          className="login-verification-btn login-submit-btn fido-touch-panel__action"
          onClick={() => setPhase("idle")}
          disabled={disabled}
        >
          Voltar
        </button>
      ) : null}
    </div>
  );
};
